import React, { useCallback, useContext, useMemo, useState } from 'react'
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Step,
  StepLabel,
  Stepper,
  Typography
} from '@mui/material'
import RequestPageIcon from '@mui/icons-material/RequestPage'
import FileDropzone from '../FileDropzone'
import NotaFiscalProdutos from '../NotaFiscalProdutos'
import ProdutoNotaDevolucao from '../../../interfaces/produtoNotaDevolucao'
import { NotaFiscalPreenchimento } from '../NotaFiscalDialog'
import { GFLContext } from '../../../infra/GFLContext'
import { TypeAlertEnum } from '../../../utils/enum/typeAlertEnum'
import axios from '../../../services/withAxios'
import { Content, DivisorStepper, BotaoImportar, Acoes } from './styles'

interface NovaNotaFiscalDialogProps {
  aberto: boolean
  setAberto: React.Dispatch<React.SetStateAction<boolean>>
  onConfirmar: (preenchimento: NotaFiscalPreenchimento, xmlFile: File) => void
}

const validarXmlParaDevolucao = async (arquivo: File): Promise<ProdutoNotaDevolucao[]> => {
  const formData = new FormData()
  formData.append('xmlFile', arquivo)

  const resposta = await axios.post('/notaFiscal/ValidarXmlParaDevolucao', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    },
    params: { avoidFeedbackMessage: true }
  })

  return (resposta.data || []) as ProdutoNotaDevolucao[]
}

const obterSugestaoNFeDevolucao = async (
  arquivo: File,
  produtos: ProdutoNotaDevolucao[]
): Promise<NotaFiscalPreenchimento> => {
  const formData = new FormData()
  formData.append('xmlFile', arquivo)
  formData.append('produtos', JSON.stringify(produtos))

  const resposta = await axios.post('/notaFiscal/ObterSugestaoDevolucao', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    },
    params: { avoidFeedbackMessage: true }
  })

  return resposta.data as NotaFiscalPreenchimento
}

const NovaNotaFiscalDialog: React.FC<NovaNotaFiscalDialogProps> = ({ aberto, setAberto, onConfirmar }) => {
  const [activeStep, setActiveStep] = useState<number>(0)
  const [nfeFile, setNFeFile] = useState<File | null>(null)
  const [produtosDisponiveis, setProdutosDisponiveis] = useState<ProdutoNotaDevolucao[]>([])
  const [produtos, setProdutos] = useState<ProdutoNotaDevolucao[]>([])
  const [carregandoImportacao, setCarregandoImportacao] = useState<boolean>(false)
  const [carregandoPreenchimento, setCarregandoPreenchimento] = useState<boolean>(false)

  const steps = ['Selecionar XML', 'Produtos']
  const contexto = useContext(GFLContext)

  const produtosNaoSelecionados = useMemo(() => {
    const numerosItensSelecionados = new Set(produtos.map((produto) => produto.numeroItem))
    return produtosDisponiveis.filter((produto) => !numerosItensSelecionados.has(produto.numeroItem))
  }, [produtosDisponiveis, produtos])

  const resetar = useCallback(() => {
    setActiveStep(0)
    setNFeFile(null)
    setProdutosDisponiveis([])
    setProdutos([])
    setCarregandoImportacao(false)
    setCarregandoPreenchimento(false)
  }, [])

  const handleFechar = () => {
    setAberto(false)
    resetar()
  }

  const handleImportarXml = async () => {
    if (!nfeFile) {
      contexto.setTypeAlert(TypeAlertEnum.error)
      contexto.setMessageAlert('Selecione um XML para poder importar.')
      contexto.setOpenAlert(true)
      return
    }

    try {
      setCarregandoImportacao(true)
      const listaProdutos = await validarXmlParaDevolucao(nfeFile)
      setProdutosDisponiveis(listaProdutos)
      setProdutos([])
      setActiveStep(1)
    } catch (e: any) {
      contexto.setTypeAlert(TypeAlertEnum.error)
      contexto.setMessageAlert(`Houve um problema ao importar o XML. Log: ${e}`)
      contexto.setOpenAlert(true)
    } finally {
      setCarregandoImportacao(false)
    }
  }

  const handleVoltar = () => {
    setActiveStep((valor) => Math.max(0, valor - 1))
  }

  const handleAvancarProdutos = async () => {
    if (produtos.length === 0 || !nfeFile) {
      return
    }

    try {
      setCarregandoPreenchimento(true)
      const preenchimento = await obterSugestaoNFeDevolucao(nfeFile, produtos)
      onConfirmar(preenchimento, nfeFile)
      setAberto(false)
      resetar()
    } catch (e: any) {
      contexto.setTypeAlert(TypeAlertEnum.error)
      contexto.setMessageAlert(`${e.response.data}`)
      contexto.setOpenAlert(true)
    } finally {
      setCarregandoPreenchimento(false)
    }
  }

  const renderSucessoStep = (index: number) => {
    if (index === 0 && activeStep >= 1) {
      return (
        <Typography variant="caption" color="green">
          Sucesso!
        </Typography>
      )
    }
    return undefined
  }

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Content component="form" id="formImportarXmlDevolucao">
            <FileDropzone
              nfeFile={nfeFile}
              setNFeFile={setNFeFile}
              required={true}
              form="formImportarXmlDevolucao"
            />
            <BotaoImportar
              disabled={carregandoImportacao || !nfeFile}
              variant="extended"
              color="primary"
              type="button"
              onClick={handleImportarXml}
            >
              <RequestPageIcon sx={{ mr: 1 }} />
              Importar
            </BotaoImportar>
          </Content>
        )
      case 1:
        return (
          <Content>
            <NotaFiscalProdutos
              produtos={produtos}
              onProdutosChange={setProdutos}
              produtosDisponiveis={produtosNaoSelecionados}
            />
            <Acoes>
              <Button onClick={handleVoltar} disabled={carregandoPreenchimento}>Voltar</Button>
              <Button
                color="success"
                onClick={handleAvancarProdutos}
                disabled={produtos.length === 0 || carregandoPreenchimento}
              >
                Próximo
              </Button>
            </Acoes>
          </Content>
        )
      default:
        return null
    }
  }

  return (
    <Dialog
      open={aberto}
      onClose={handleFechar}
      maxWidth={activeStep === 0 ? 'sm' : 'lg'}
      fullWidth
    >
      <DialogTitle>Nova Nota Fiscal</DialogTitle>
      <DialogContent>
        <DivisorStepper />
        <Stepper activeStep={activeStep}>
          {steps.map((label, index) => (
            <Step key={label}>
              <StepLabel optional={renderSucessoStep(index)}>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        <DivisorStepper />
        {renderStepContent()}
      </DialogContent>
    </Dialog>
  )
}

export default NovaNotaFiscalDialog
